import React, {useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import '../../home/components/NewUser/NewUser.scss';
import PropTypes from 'prop-types';
import {useFormik} from 'formik';
import {useHistory} from 'react-router-dom';
import actions from '../../redux/actions';
import * as yup from 'yup';
import _ from 'lodash';
import Loading from '../../common/components/Loading/Loading';
import CustomAlert from '../../common/components/Alert/CustomAlert';
import {deleteExtraSpaces} from '../../utils/util';
import CustomInput from '../../common/components/CustomInput/CustomInput';
import CustomSelect from '../../common/components/CustomSelect/CustomSelect';
import Button from '../../common/components/Button/Button';
import ArrowBackComponent from '../../common/components/ArrowBack/ArrowBack';
import {CustomPhone} from '../../common/components/CustomPhone/CustomPhone';
import {FormControlLabel, Radio, RadioGroup, Switch} from '@mui/material';
import InfoTooltip from '../../common/components/InfoTooltip/InfoTooltip';
import {VER_MONTOS_TOOLTIP} from './permissionTooltips';

export const InputErrorMessage = ({formik, name, className = ''}) => {
    return (
        <div className={'form-error ' + className}>
            {_.get(formik.touched, name) && _.get(formik.errors, name)}
        </div>
    );
};

// La opción de permisos es, en realidad, el rol del usuario de sede
const permissionByRoleName = {
    'PROVIDER_USER_VIEWER': 'VIEW_AFFILIATES',
    'PROVIDER_USER_QUOTER': 'QUOTE',
};

const roleByPermission = {
    'VIEW_AFFILIATES': 'USER_VIEWER',
    'QUOTE': 'USER_QUOTER',
    'ALL': 'USER',
};

const EditProviderUser = (props) => {
    const {
        getBranches,
        getBranchesProviders,
        branches,
        branchesProviders,
        providerSelected,
        putProvider,
        editProviderState,
        cleanEditUserState,
        getBranchesState,
        getBranchesProvidersState,
        cleanBranches,
        cleanProvidersBranches,
        showSidebar,
    } = props;

    const validationSchema =
        yup.object({
            name: yup
                .string('Ingrese nombre')
                .required('Ingrese nombre'),
            salesforceProviderId: yup
                .string('Seleccione un prestador')
                .required('Seleccione un prestador'),
            salesforceBranchId: yup.string().when('type', {
                is: 'PROVIDER_USER',
                then: yup.string('Seleccione una sede')
                    .required('Seleccione una sede'),
                otherwise: yup.string(),
            }),
            phoneNumbers: yup.array().of(
                yup.object().shape({
                    type: yup
                        .string('Ingrese tipo')
                        .required('Ingrese tipo'),
                    number: yup.string().when('type', {
                        is: 'OTHER',
                        then: yup.string('Ingrese número')
                            .required('Ingrese número'),
                        otherwise: yup.string('Ingrese número')
                            .matches(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
                                'Número Inválido (No olvide colocar el código de área)')
                            .required('Ingrese número')}),
                }),
            )
                .required('Teléfonos requeridos'),
            permission: yup.string().optional(),
        });

    const history = useHistory();
    const roleName = providerSelected?.user?.role?.name;
    // A un desarrollador no se le puede cambiar el rol (el backend también lo valida)
    const isDeveloper = roleName === 'DEVELOPER';

    const formik = useFormik({
        initialValues: {
            id: providerSelected?.id || '',
            type: roleName === 'PROVIDER_ADMIN' ? 'PROVIDER_ADMIN' :
                roleName === 'DEVELOPER' ? 'DEVELOPER' : 'PROVIDER_USER',
            name: providerSelected?.name || '',
            cuitOrCuil: providerSelected?.cuitOrCuil || '',
            email: providerSelected?.user?.email || '',
            salesforceProviderId: providerSelected?.salesforceProviderId || '',
            salesforceBranchId: providerSelected?.salesforceBranchId || '',
            phoneNumbers: providerSelected?.phoneNumbers?.length ?
                providerSelected.phoneNumbers :
                [{type: 'PERSONAL', number: ''}],
            permission: permissionByRoleName[roleName] || 'ALL',
            // Si Salesforce no devuelve el dato de la sede se asume que puede ver montos
            verMontoValidador: providerSelected?.verMontoValidador !== false,
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            const isBranchUser = values.type === 'PROVIDER_USER';
            // El prestador y el cuit no se pueden cambiar: no se envían
            const user = {
                name: values.name,
                phoneNumbers: _.compact(values.phoneNumbers),
                // A un desarrollador no se le envía rol: no se le puede cambiar
                ...(values.type === 'DEVELOPER' ? {} : isBranchUser ? {
                    role: roleByPermission[values.permission],
                    salesforceBranchId: values.salesforceBranchId,
                    verMontoValidador: values.verMontoValidador,
                } : {
                    // Solo puede haber un admin por prestador: el backend lo valida
                    role: 'ADMIN',
                }),
            };
            putProvider({id: values.id, user});
        },
    });

    useEffect(() => {
        cleanEditUserState();
        cleanBranches();
        cleanProvidersBranches();
        getBranches();
        // eslint-disable-next-line
    }, []);

    useEffect(
        () => {
            if (editProviderState.success) {
                cleanEditUserState();
                history.goBack();
            }
            // eslint-disable-next-line
        }, [editProviderState],
    );

    // La sede que hay que dejar seleccionada cuando llegan las sedes del prestador
    const desiredBranchId = useRef(providerSelected?.salesforceBranchId || '');

    useEffect(() => {
        if (formik.values.type === 'PROVIDER_USER' && formik.values.salesforceProviderId) {
            getBranchesProviders(formik.values.salesforceProviderId);
        }
        // eslint-disable-next-line
    }, [formik.values.salesforceProviderId, formik.values.type]);

    useEffect(() => {
        setBranchesOptions(branches?.map((branch) => {
            if (branch.providerId === 'a1l2K000005fIfrQAE') {
                branch.fullName = 'Prestador sin sede';
            }
            return {value: branch.providerId.toString(), label: deleteExtraSpaces(branch.fullName)};
        }));
    }, [branches, getBranchesState.success]);

    useEffect(() => {
        const options = branchesProviders?.map((branch) => {
            return {value: branch.branchId.toString(), label: deleteExtraSpaces(branch.fullName)};
        }) || [];
        setBranchesProvidersOptions(options);
        if (!options.length) return;
        const isDesiredBranchValid = options.some((option) => option.value === desiredBranchId.current);
        const nextBranchId = isDesiredBranchValid ? desiredBranchId.current : (options[0]?.value || '');
        desiredBranchId.current = nextBranchId;
        formik.setFieldValue('salesforceBranchId', nextBranchId);
        // eslint-disable-next-line
    }, [branchesProviders, getBranchesProvidersState.success]);

    const [branchesOptions, setBranchesOptions] = useState([]);
    const [branchesProvidersOptions, setBranchesProvidersOptions] = useState([]);

    const handleArrowBack = () => {
        history.goBack();
    };

    if (!providerSelected?.id) {
        return <CustomAlert text={'No se encontró el usuario de prestador a editar'} severity='error'/>;
    }

    return (getBranchesState.success ?
        <div className={showSidebar && window.innerWidth <= 700 ? 'ul-div-background bg-mt-10' : 'ul-div-background'}
             style={{backgroundColor: 'transparent', margin: '10px', padding: '10px', paddingRight: '40px'}}>
            <div className="ul-div-sect-header">
                <div style={{display: 'flex'}}>
                    <div style={{width: '40px', height: '40px', marginRight: '20px'}}>
                        <ArrowBackComponent onClick={handleArrowBack}/>
                    </div>
                    <p className="h3 v2-c-text-title">Editar Usuario de Prestador</p>
                </div>
                <div className="eu-radio-container" style={{marginLeft: '20px'}}>
                    <input
                        type='radio'
                        value={'PROVIDER_ADMIN'}
                        name='type'
                        className="eu-radio"
                        onChange={(e) => formik.setFieldValue('type', e.target.value)}
                        disabled={isDeveloper}
                        checked={formik.values.type === 'PROVIDER_ADMIN'}
                    />
                    Admin de prestador
                    <input
                        type='radio'
                        value={'PROVIDER_USER'}
                        name='type'
                        className="eu-radio"
                        onChange={(e) => formik.setFieldValue('type', e.target.value)}
                        disabled={isDeveloper}
                        checked={formik.values.type === 'PROVIDER_USER'}
                    /> Usuario de sede
                    <input
                        type='radio'
                        value={'DEVELOPER'}
                        name='type'
                        className="eu-radio"
                        disabled={true}
                        checked={formik.values.type === 'DEVELOPER'}
                    /> Desarrollador
                </div>
            </div>

            <div className="ul-div-form">
                <form className="eu-form" onSubmit={formik.handleSubmit}>
                    <div className="eu-div-container">
                        <div className="eu-div-input">
                            <CustomInput
                                label="Nombre"
                                placeholder="Nombre"
                                onChange={(value) => {
                                    formik.setFieldValue('name', value);
                                }}
                                value={formik.values.name}
                                type={'text'}
                            />
                            <CustomInput
                                label="Cuit/Cuil"
                                placeholder="Cuit/Cuil"
                                onChange={() => {}}
                                value={formik.values.cuitOrCuil}
                                disabled={true}
                                type={'cuit'}
                            />
                        </div>
                        <div className="eu-div-input">
                            <InputErrorMessage formik={formik} name='name' className='eu-input-l'/>
                        </div>
                        <div className="eu-div-input">
                            <CustomInput
                                label="Email"
                                placeholder="Email"
                                value={formik.values.email}
                                onChange={() => {}}
                                disabled={true}
                                type={'text'}
                            />
                            <CustomSelect
                                label={'Prestador'}
                                placeholder={'Prestador'}
                                onChange={() => {}}
                                options={branchesOptions}
                                value={formik.values.salesforceProviderId}
                                disabled={true}
                                isClearable={false}
                            />
                        </div>
                        <div className="eu-div-input">
                            <InputErrorMessage formik={formik} name='email' className='eu-input-l'/>
                            <InputErrorMessage formik={formik} name='salesforceProviderId' className='eu-input-s'/>
                        </div>
                        {formik.values.type === 'PROVIDER_USER' &&
                            <>
                                <div className="eu-div-input">
                                    <CustomSelect
                                        label={'Sede'}
                                        placeholder={'Sede'}
                                        onChange={(value) => {
                                            desiredBranchId.current = value || '';
                                            formik.setFieldValue('salesforceBranchId', value);
                                        }}
                                        options={branchesProvidersOptions}
                                        value={formik.values.salesforceBranchId}
                                        isSearchable={true}
                                    />
                                </div>
                                <div className="eu-div-input">
                                    <InputErrorMessage formik={formik} name='salesforceBranchId' className='eu-input-l'/>
                                </div>
                            </>
                        }
                        <CustomPhone formik={formik}/>
                        {formik.values.type === 'PROVIDER_USER' &&
                            <div className="eu-div-input">
                                <div className="v2-input-div">
                                    <p className='body-3 c-text-subtitle'>Permisos</p>
                                    <RadioGroup
                                        onChange={(e) => formik.setFieldValue('permission', e.target.value)}
                                        value={formik.values.permission}
                                        name='permissions'>
                                        <FormControlLabel value="ALL" control={<Radio/>} label="Todos los permisos"/>
                                        <FormControlLabel value="QUOTE" control={<Radio/>} label="Consultar coberturas y cotizaciones"/>
                                        <FormControlLabel value="VIEW_AFFILIATES" control={<Radio/>} label="Consultar coberturas"/>
                                    </RadioGroup>
                                </div>
                                <div className="v2-input-div">
                                    <p className='body-3 c-text-subtitle' style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                                        Puede ver montos
                                        <InfoTooltip title={VER_MONTOS_TOOLTIP}/>
                                    </p>
                                    <Switch
                                        checked={formik.values.verMontoValidador}
                                        onChange={(e) => formik.setFieldValue('verMontoValidador', e.target.checked)}
                                    />
                                </div>
                            </div>
                        }
                        <div className="eu-div-button">
                            <Button type="submit" size="medium">Guardar cambios</Button>
                        </div>
                    </div>
                </form>
            </div>
        </div> :
        getBranchesState.loading ? <Loading className="ul-div-sect-table"/> :
            <CustomAlert text={'planes' || String(getBranchesState.error)} severity='error'/>);
};

const mapStateToProps = (state) => ({
    branches: state.home.branches,
    branchesProviders: state.home.branchesProviders,
    providerSelected: state.home.providerSelected,
    editProviderState: state.home.editProviderState,
    getBranchesState: state.home.getBranchesState,
    getBranchesProvidersState: state.home.getBranchesProvidersState,
    showSidebar: state.common.showSidebar,
});

const mapDispatchToProps = (dispatch) => ({
    getBranches: () => dispatch(actions.home.getBranchesRequest()),
    getBranchesProviders: (id) => dispatch(actions.home.getBranchesProvidersRequest(id)),
    putProvider: (body) => dispatch(actions.home.putProviderRequest(body)),
    cleanEditUserState: () => dispatch(actions.home.cleanEditUserState()),
    cleanBranches: () => dispatch(actions.home.cleanBranches()),
    cleanProvidersBranches: () => dispatch(actions.home.cleanProvidersBranches()),
});

EditProviderUser.propTypes = {
    providerSelected: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditProviderUser);
